'use client'

import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useSession } from "next-auth/react"
import { toast } from "@/lib/toast"
import { useRouter, usePathname } from "next/navigation"
import { navigateUrl } from "@/utils/client-request"
import { usePaymentChecker, useInAppBrowser, IN_APP_BROWSER_LABELS, type PaymentEndpoint } from "@/lib/commerce/hooks"
import { FREEFORM_ADDRESS_CHANNELS, STATUS_TONES, STATUS_LABELS, TERMINAL_STATUSES, PAYMENT_METHOD_LABELS, DIGITAL_DOWNLOAD_STATUSES, formatStepDate, formatAddressType, formatOrderAddress, getCustomerName, getVariantDimension, isCustomFieldVisible, getProductPricing, calcPriceBreakdown, buildDigitalDownloadUrl, downloadDigitalFile, bookloDisplayStatus, showsReadyStep, calcDepositBreakdown, getPayableAmount, hasLiveInvoice, resolveOrderStatus, DEPOSIT_LABELS, getOmniwayQrImage } from "@/utils/order"
import DepositBalanceCard from "@/components/blocks/DepositBalanceCard"
import QRCodeImport from "react-qr-code"
const QRCode = QRCodeImport as unknown as React.FC<{ value: string, size?: number, fgColor?: string, bgColor?: string, style?: React.CSSProperties }>
import { Button } from "@/components/elements/Button"
import { bankList, formatDateLong, formatDateNormal, formatDateTimeLong, formatImageURL, formatNumber, formatPrice, isCustomerAuthActive, resolveColorHex } from "@/utils/util"
import { uploadURL } from "@/lib/customFields"
import { CirclePercent, Clock, Coins, Copy, Download, Gift, HandCoins, Receipt, CircleCheck, Package, PackageCheck, Truck, Home, Check, XCircle, RotateCcw, ShieldCheck, Info, User as UserIcon, Phone, Mail, MapPin, Store, ChevronDown, CreditCard, Zap, ExternalLink, Smartphone, Sparkles, Printer, MessageCircle, ArrowRight, Wallet } from "lucide-react"
import Image from "next/image"
import qpayLogo from "@/assets/qpay.svg"
import pocketzeroLogo from "@/assets/pocketzero.svg"
import storepayLogo from "@/assets/storepay.png"
import lendmnLogo from "@/assets/lendmn.png"
import omniwayLogo from "@/assets/omniway.png"

// productImage stores one bucket object name, or an array of them for multi-select fields.
// Buyer-supplied, so anything uploadURL cannot place in our bucket is dropped.
function customFieldImages(value: any): string[] {
  return (Array.isArray(value) ? value : [value]).map(image => uploadURL(image)).filter((url): url is string => !!url)
}

function renderCustomFieldValue(field: any, dateFormatter: (v: any) => string) {
  if (field.type === 'checkbox') return field.value === 'true' || field.value === true ? 'Тийм' : 'Үгүй'
  if (field.type === 'date') return dateFormatter(field.value)
  if (field.type === 'file') {
    const url = uploadURL(field.value)
    return url && <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Файл харах</a>
  }
  if (field.type === 'productImage') return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {customFieldImages(field.value).map((image: string) => (
        <a key={image} href={image} target="_blank" rel="noopener noreferrer">
          <img src={image} alt="" className="w-10 h-10 rounded object-cover bg-muted" />
        </a>
      ))}
    </span>
  )
  return field.value
}

/* ---------- Small UI primitives ---------- */

function StatusPill({ status, order }: { status: string, order?: any }) {
  // 'pending' means waiting for money — not true of an order with nothing owed up front.
  // See resolveOrderStatus.
  const shown = resolveOrderStatus(order, status)
  const tone = STATUS_TONES[shown] || STATUS_TONES.pending
  const label = STATUS_LABELS[shown] || 'Тодорхойгүй'
  return (
    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: tone.bg, color: tone.fg }}>
      <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: tone.dot }}>
        {shown === 'pending' && (
          <span className="ping-ring" style={{ color: tone.dot }} />
        )}
      </span>
      {label}
    </span>
  )
}

function InfoRow({ icon: Icon, label, children }: { icon: any, label: string, children: any }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 grid sm:grid-cols-[160px_1fr] gap-0.5 sm:gap-3 items-baseline">
        <div className="text-xs text-muted-foreground font-medium">{label}</div>
        <div className="text-sm font-medium wrap-break-word">{children}</div>
      </div>
    </div>
  )
}

function CopyField({ label, value, mono }: { label: string, value: string, mono?: boolean }) {
  const [flashed, setFlashed] = useState(false)
  const handle = () => {
    if (!value) return toast.error('Хуулах утга байхгүй байна')
    navigator.clipboard?.writeText(value)
    setFlashed(true)
    setTimeout(() => setFlashed(false), 900)
  }
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">{label}</div>
      <button
        type="button"
        onClick={handle}
        className="group w-full flex items-center gap-2 bg-white rounded-lg border border-border px-3 py-2.5 text-left hover:border-blue-300 hover:bg-zinc-50 transition-colors"
        style={flashed ? { background: '#dbeafe' } : undefined}
      >
        <span className={'flex-1 text-sm text-foreground ' + (mono ? 'font-mono tabular-nums font-semibold break-all' : 'font-semibold')}>{value}</span>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground group-hover:text-blue-600 transition-colors font-semibold shrink-0">
          {flashed ? <><Check className="w-3.5 h-3.5" />Хуулсан</> : <><Copy className="w-3.5 h-3.5" />Хуулах</>}
        </span>
      </button>
    </div>
  )
}

/* ---------- Header ---------- */

function OrderTitle({ order, status }: { order: any, status: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground font-semibold mb-1">Захиалга</div>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex flex-wrap items-center gap-3 text-foreground">
        #{order?.customId}
        <StatusPill status={status} order={order} />
      </h1>
      <div className="text-sm text-muted-foreground mt-1">
        {formatDateTimeLong(order?.createdAt)}
        {order?.products?.length ? ` · ${order.products.length} бараа` : ''}
        {getCustomerName(order) ? ` · ${getCustomerName(order)}` : ''}
      </div>
    </div>
  )
}

function OrderStepper({ order, vendor, status, primary }: { order: any, vendor: any, status: string, primary: string }) {
  const isPickup = order?.type === 'pickup'
  const isRefundLike = status === 'refunded' || status === 'partiallyRefunded'

  if (TERMINAL_STATUSES.has(status) || isRefundLike) {
    const tone = STATUS_TONES[status] || STATUS_TONES.refunded
    const Icon = isRefundLike ? RotateCcw : XCircle
    const stamp = status === 'canceled' ? order?.canceledAt
      : isRefundLike ? order?.refundedAt
        : status === 'deliveryFailed' ? order?.deliveryFailedAt : null
    const reason = status === 'canceled' ? order?.canceledReason
      : isRefundLike ? order?.refundedReason
        : status === 'deliveryFailed' ? order?.deliveryFailedReason : null
    const subText = status === 'canceled' ? 'Захиалга цуцлагдсан'
      : isRefundLike ? 'Буцаалт хийгдсэн'
        : 'Хүргэлт амжилтгүй болсон'
    return (
      <div className="rounded-xl border bg-white px-4 py-4 flex items-center gap-3" style={{ borderColor: tone.ring }}>
        <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: tone.bg, color: tone.fg }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            {STATUS_LABELS[status]}
            {reason ? <span className="text-xs ml-1.5 font-normal text-foreground">({reason})</span> : null}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {subText}
            {stamp ? ` · ${formatDateTimeLong(stamp)}` : ''}
          </div>
        </div>
      </div>
    )
  }

  // Pay-after-delivery reverses the last two steps: the goods arrive, then the
  // money. Payment gets its own trailing step keyed off paidAt, since order.status
  // never passes through 'paid' for these.
  const isOnDelivery = order?.payAfterDelivery
  const baseSteps = isOnDelivery ? [
    { key: 'pending', label: 'Захиалсан', icon: Receipt, at: order?.createdAt },
    { key: 'ready', label: 'Бэлэн', icon: Package, at: order?.readyAt },
    { key: 'shipped', label: 'Хүргэлтэнд', icon: Truck, at: order?.shippedAt },
    { key: 'delivered', label: 'Хүргэгдсэн', icon: Home, at: order?.deliveredAt },
    { key: 'settled', label: 'Төлсөн', icon: CircleCheck, at: order?.paidAt },
  ] : [
    { key: 'pending', label: 'Захиалсан', icon: Receipt, at: order?.createdAt },
    { key: 'paid', label: 'Төлсөн', icon: CircleCheck, at: order?.paidAt },
    { key: 'ready', label: 'Бэлэн', icon: Package, at: order?.readyAt },
    { key: 'shipped', label: 'Хүргэлтэнд', icon: Truck, at: order?.shippedAt },
    { key: 'delivered', label: 'Хүргэгдсэн', icon: Home, at: order?.deliveredAt },
  ]
  const hasReadyStep = showsReadyStep(order, vendor)
  /*
    Split payment gets its own step. Without it, "deposit paid, waiting on the goods"
    looks identical to "nothing has happened yet" — and that is the state a pre-order
    sits in for its entire lead time, so it is the one the customer checks on most.
  */
  const isDeposit = !!(order?.depositAmount > 0)
  const withDeposit = isDeposit
    ? [baseSteps[0], { key: 'deposit', label: 'Урьдчилгаа', icon: Wallet, at: order?.depositPaidAt }, ...baseSteps.slice(1)]
    : baseSteps
  const steps = withDeposit.filter(s => !(isPickup && s.key === 'shipped') && !(!hasReadyStep && s.key === 'ready'))
  // `status` is the Booklo-aware displayStatus, so the stepper advances correctly.
  const targetKey = status === 'partiallyRefunded' ? 'delivered' : status
  let cur = Math.max(0, steps.findIndex(s => s.key === targetKey))
  // The deposit landing is real progress, but the order is still 'pending' — nothing in
  // the status ladder would move the marker off step 0 on its own.
  if (isDeposit && order?.depositPaidAt && cur < 1) cur = 1
  /*
    Payment sits last in this layout because a pay-after-delivery order settles at the
    door — so paidAt implied everything before it was done. A split payment breaks that:
    the customer can clear the balance days before the goods move, and jumping the marker
    to the end painted "Хүргэлтэнд" and "Хүргэгдсэн" as complete when neither had
    happened. Only advance when the goods actually went out; the settled step still ticks
    on its own via the `at` rule below.
  */
  if (isOnDelivery && order?.paidAt && (status === 'delivered' || order?.deliveredAt)) cur = steps.length - 1
  const prog = (cur / (steps.length - 1)) * 100

  return (
    <div className="rounded-xl border border-border bg-white px-4 sm:px-6 pt-5 pb-4">
      <div className="relative">
        {/* inset to the first/last circle centers so the gradient stop lands dead-centre on the active icon */}
        <div
          className="absolute top-4 h-0.5 rounded-full"
          style={{
            left: `${50 / steps.length}%`,
            right: `${50 / steps.length}%`,
            background: `linear-gradient(to right, ${primary} ${prog}%, var(--border) ${prog}%)`,
          }}
        />
        <ol className="relative grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
          {steps.map((s, i) => {
            // A step past the marker that carries its own timestamp really did happen —
            // paying the balance early ticks Төлсөн without implying delivery.
            const done = i < cur || (i > cur && !!s.at)
            const active = i === cur
            const Icon = s.icon
            return (
              <li key={s.key} className="flex flex-col items-center text-center">
                <div
                  className={
                    'relative w-8 h-8 rounded-full grid place-items-center text-xs font-semibold transition-colors '
                    + (done ? ''
                      : active ? 'bg-white ring-2'
                        : 'bg-white border border-border text-muted-foreground')
                  }
                  style={
                    done ? { background: primary, color: '#ffffff' }
                      : active ? { color: primary, boxShadow: `0 0 0 2px ${primary}` }
                        : undefined
                  }
                >
                  {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  {active && (
                    <span className="ping-ring" style={{ color: primary }} />
                  )}
                </div>
                <div className={'text-[11px] sm:text-xs mt-2 font-semibold ' + (done || active ? 'text-foreground' : 'text-muted-foreground')}>{s.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{s.at ? formatStepDate(s.at) : '—'}</div>
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}

/* ---------- Pending payment ---------- */

function DigitalDownloadRow({ order, vendor, primary }: { order: any, vendor: any, primary: string }) {
  const [downloading, setDownloading] = useState(false)
  const digitalProducts = (order?.products || []).filter((p: any) => {
    const productData = p.product || p
    return productData?.isDigital && p.variant?.digitalFile
  })
  if (!digitalProducts.length || !DIGITAL_DOWNLOAD_STATUSES.has(order?.status)) return null

  const single = digitalProducts.length === 1
  const firstStatus = single
    ? (order?.digitalDownloadStatus || []).find((s: any) => s?.variantId === digitalProducts[0].variant.id) || null
    : null
  const firstUnavailable = !!firstStatus && (firstStatus.expired || firstStatus.downloadsRemaining === 0)
  const firstUrl = single && !firstUnavailable ? buildDigitalDownloadUrl(order, vendor, digitalProducts[0].variant.id) : null

  return (
    <div className="rounded-xl border border-border bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 grid place-items-center shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">Дижитал бүтээгдэхүүн татах</div>
          <div className="text-xs text-muted-foreground">
            {single ? 'Доорх товчлуурыг дарж файлаа татаж авна уу.' : `${digitalProducts.length} файл бэлэн боллоо. Дээрх жагсаалтаас тус бүрд нь татна уу.`}
          </div>
        </div>
      </div>
      {single && firstUrl && (
        <div className="flex flex-col items-center sm:items-end gap-1.5 shrink-0">
          <Button tag="button" isLoading={downloading} loadingMessage="Татаж байна..." onClick={async () => { setDownloading(true); await downloadDigitalFile(firstUrl); setDownloading(false) }} color={primary} className="h-8! px-3! text-xs! w-fit">
            <Download className="w-4 h-4" />
            <span>Файл татах</span>
          </Button>
          {firstStatus && !firstStatus.expired && (
            <div className="text-[11px] text-muted-foreground tabular-nums">
              Үлдсэн: <span className="font-semibold text-foreground">{firstStatus.downloadsRemaining}/{firstStatus.downloadsMax}</span>
              {firstStatus.expiresAt && (
                <span> · {formatDateNormal(new Date(firstStatus.expiresAt))} хүртэл</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RefundBanner({ order }: { order: any }) {
  const status = order?.status
  if (status !== 'refunded' && status !== 'partiallyRefunded') return null
  const isPartial = status === 'partiallyRefunded'
  const refundedAmount = order?.totalRefunded || (isPartial ? 0 : order?.amount)
  return (
    <div className="rounded-xl border border-border bg-zinc-50 p-4 flex items-start gap-3">
      <RotateCcw className="w-4 h-4 text-muted-foreground mt-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground">
          {isPartial ? 'Хэсэгчилсэн буцаалт' : 'Бүрэн буцаалт'}
          {order?.refundedReason ? <span className="ml-1.5 text-xs font-normal">({order.refundedReason})</span> : null}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          Таны {formatPrice(refundedAmount)} нь 3-5 ажлын өдөрт буцаагдах болно. Хэрэв хүлээн аваагүй тохиолдолд дэлгүүртэй холбогдоно уу.
        </div>
      </div>
    </div>
  )
}

function FooterActions({ vendor, pathname }: { vendor: any, pathname: string }) {
  const { data: session } = useSession()
  const customerLoggedIn = !!session?.user?.id
  const showMyOrders = !isCustomerAuthActive(vendor) || customerLoggedIn
  return (
    <div className="pt-2 flex flex-wrap gap-3 justify-center items-center print:hidden">
      <button onClick={() => window.print()} className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors">
        <Printer className="w-3.5 h-3.5" />Хэвлэх
      </button>
      <span className="text-border">·</span>
      <a href="https://help.shop.mn" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors">
        <MessageCircle className="w-3.5 h-3.5" />Тусламж
      </a>
      {showMyOrders && (
        <>
          <span className="text-border">·</span>
          <a href={navigateUrl('/order', pathname)} className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors">
            Миний захиалгууд<ArrowRight className="w-3.5 h-3.5" />
          </a>
        </>
      )}
    </div>
  )
}

function PrintableReceipt({ order, vendor }: { order: any, vendor: any }) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  if (!mounted) return null
  return createPortal(<PrintableReceiptBody order={order} vendor={vendor} />, document.body)
}

function PrintableReceiptBody({ order, vendor }: { order: any, vendor: any }) {
  const { salePriceSavings, discountBreakdown } = calcPriceBreakdown(order)
  const totalDue = (order?.amount || 0) - (order?.totalRefunded || 0)
  const isDelivery = order?.type === 'delivery'
  const orderProducts = order?.products || []
  const allDigital = orderProducts.length > 0 && orderProducts.every((p: any) => (p.product || p)?.isDigital === true)
  const showAddressOrBranch = !allDigital
  const customerName = getCustomerName(order) || '-'
  const addressText = isDelivery
    ? formatOrderAddress(order?.address, order?.channel)
    : (order?.branchData || order?.branch)
      ? `${order?.branchData?.name || order?.branch?.name}, ${order?.branchData?.address || order?.branch?.address}`
      : '-'
  return (
    <div data-print-root className="hidden print:block text-black text-[12px] leading-snug">
      <style>{`@page { size: A4; margin: 16mm; }`}</style>
      <div className="text-center mb-4">
        <div className="text-lg font-bold">{vendor?.name || 'Shop.mn'}</div>
        <div className="text-[11px]">Захиалгын баримт</div>
      </div>
      <div className="flex justify-between border-y border-black py-2 mb-3">
        <div>
          <div className="font-bold">Захиалга #{order?.customId}</div>
          <div className="text-[11px]">{formatDateTimeLong(order?.createdAt)}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px]">Төлөв</div>
          <div className="font-bold">{STATUS_LABELS[order?.status] || '-'}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Хүлээн авагч</div>
          <div>{customerName}</div>
          <div>{order?.phone}{order?.additionalPhone ? ` · ${order.additionalPhone}` : ''}</div>
          {order?.email && <div>{order.email}</div>}
        </div>
        {showAddressOrBranch && (
          <div>
            <div className="text-[10px] uppercase font-bold mb-1">{isDelivery ? 'Хүргэх хаяг' : 'Авах салбар'}</div>
            <div>{addressText}</div>
          </div>
        )}
      </div>
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="border-y border-black">
            <th className="text-left py-1 font-bold">Бараа</th>
            <th className="text-right py-1 font-bold w-16">Тоо</th>
            <th className="text-right py-1 font-bold w-24">Үнэ</th>
            <th className="text-right py-1 font-bold w-24">Дүн</th>
          </tr>
        </thead>
        <tbody>
          {(order?.products || []).map((p: any, i: number) => {
            const productData = p.product || p
            const productName = productData?.name || p?.name || 'Бүтээгдэхүүн'
            const { unitPrice } = getProductPricing(p)
            const qty = p?.quantity || 0
            return (
              <tr key={i} className="border-b border-zinc-300 align-top">
                <td className="py-1 pr-2">
                  {productName}
                  {p.variant?.name && <div className="text-[10px]">{p.variant.name}</div>}
                </td>
                <td className="py-1 text-right tabular-nums">{qty}</td>
                <td className="py-1 text-right tabular-nums">{formatPrice(unitPrice)}</td>
                <td className="py-1 text-right tabular-nums">{formatPrice(unitPrice * qty)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="ml-auto w-1/2 text-[11px]">
        <div className="flex justify-between py-0.5"><span>Бараанын дүн</span><span className="tabular-nums">{formatPrice(order?.subAmount)}</span></div>
        {salePriceSavings > 0 && <div className="flex justify-between py-0.5"><span>Хямдрал</span><span className="tabular-nums">-{formatPrice(salePriceSavings)}</span></div>}
        {discountBreakdown.map(d => (
          <div key={d.id} className="flex justify-between py-0.5"><span>Урамшуулал ({d.name})</span><span className="tabular-nums">-{formatPrice(d.amount)}</span></div>
        ))}
        {(order?.couponAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Купон{order?.couponData?.name ? ` (${order.couponData.name})` : ''}</span><span className="tabular-nums">-{formatPrice(order?.couponAmount)}</span></div>
        )}
        {(order?.giftCardAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Бэлгийн карт</span><span className="tabular-nums">-{formatPrice(order?.giftCardAmount)}</span></div>
        )}
        {(order?.loyaltyDiscountAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Лоялти хөнгөлөлт</span><span className="tabular-nums">-{formatPrice(order?.loyaltyDiscountAmount)}</span></div>
        )}
        {(order?.loyaltyVoucherAmount || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Лоялти ваучер</span><span className="tabular-nums">-{formatPrice(order?.loyaltyVoucherAmount)}</span></div>
        )}
        {isDelivery && (
          <div className="flex justify-between py-0.5"><span>Хүргэлт</span><span className="tabular-nums">{vendor?.deliveryType === 'laterDelivery' && order?.deliveryPrice === 0 ? 'Нэмэлтээр тооцно' : order?.deliveryPrice === 0 ? 'Үнэгүй' : formatPrice(order?.deliveryPrice)}</span></div>
        )}
        {(order?.totalRefunded || 0) > 0 && (
          <div className="flex justify-between py-0.5"><span>Буцаалт</span><span className="tabular-nums">-{formatPrice(order?.totalRefunded)}</span></div>
        )}
        <div className="flex justify-between border-t border-black mt-1 pt-1 font-bold text-[13px]">
          <span>Нийт төлөх</span>
          <span className="tabular-nums">{formatPrice(totalDue)}</span>
        </div>
      </div>
      <div className="mt-6 text-center text-[10px]">
        Энэхүү баримтыг {formatDateTimeLong(new Date())}-нд хэвлэв · {vendor?.name || 'Shop.mn'}
      </div>
    </div>
  )
}

function PaidConfirmation({ order, status, primary }: { order: any, status: string, primary: string }) {
  const pathname = usePathname()
  const isDelivered = status === 'delivered'
  const isShipped = status === 'shipped'
  const overlay = 'rgba(255,255,255,0.10)'
  const overlayStrong = 'rgba(255,255,255,0.20)'
  // 'Төлбөр амжилттай' is only true when money has actually arrived — a
  // pay-after-delivery order reaches 'ready' without it.
  const isUnpaidOnDelivery = order?.payAfterDelivery && !order?.paidAt
  // Split payment: the deposit landed but the balance has not.
  const deposit = calcDepositBreakdown(order)
  const Icon = isDelivered ? PackageCheck : isShipped ? Truck : Sparkles
  const eyebrow = isDelivered ? 'Захиалга хүргэгдсэн' : isShipped ? 'Хүргэлтэнд гарсан' : deposit.isAwaitingBalance ? 'Урьдчилгаа төлөгдсөн' : isUnpaidOnDelivery ? 'Захиалга баталгаажсан' : 'Төлбөр амжилттай'
  const title = isDelivered ? 'Танд баярлалаа' : isShipped ? 'Захиалга замдаа явж байна' : 'Захиалга баталгаажигдлаа'
  const subtitle = isDelivered ? 'Бараа тань таалагдсан бол үнэлгээ бичиж үлдээгээрэй!'
    : isShipped ? 'Таны захиалга хүргэлтэнд гарсан. Хүргэлтийн явцыг эндээс хянаарай.'
      : 'Бид бараагаа бэлтгэж эхэллээ. Төлөв өөрчлөгдөхөд танд мэдэгдэнэ.'
  return (
    <div className="relative overflow-hidden rounded-2xl px-5 sm:px-7 py-6 sm:py-8" style={{ background: primary, color: '#ffffff' }}>
      <div className="absolute -top-6 -right-6 w-36 h-36 rounded-full blur-2xl" style={{ background: overlay }} />
      <div className="absolute -bottom-8 -left-6 w-32 h-32 rounded-full blur-xl" style={{ background: overlay }} />
      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: overlayStrong }}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>
            {eyebrow}
          </div>
          <div className="text-xl sm:text-2xl font-bold mt-1 leading-tight">
            {title}
          </div>
          <div className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>
            {subtitle}
          </div>
        </div>
      </div>
      <div className="relative mt-5 grid sm:grid-cols-3 gap-3">
        {[
          { label: 'Захиалга', value: `#${order?.customId}`, mono: false },
          // What was actually handed over, not what the order is worth — on a split
          // payment those differ, and order.amount here would tell a customer who paid
          // 20% that they have paid in full.
          { label: 'Төлсөн дүн', value: formatPrice(deposit.paidSoFar) || '0₮', mono: true },
          // While a balance is outstanding it displaces the delivery-date tile: it is
          // the one thing the customer still has to act on.
          deposit.isAwaitingBalance
            ? { label: 'Үлдэгдэл', value: formatPrice(deposit.outstanding) || '0₮', mono: true }
            : { label: isDelivered ? 'Хүргэгдсэн' : 'Хүргэгдэх', value: isDelivered ? (order?.deliveredAt ? formatDateTimeLong(order.deliveredAt) : '—') : 'Удахгүй', mono: false },
        ].map((tile, i) => (
          <div key={i} className="rounded-lg backdrop-blur px-3 py-2.5" style={{ background: overlay }}>
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>{tile.label}</div>
            <div className={'text-sm font-semibold mt-0.5 ' + (tile.mono ? 'tabular-nums' : '')}>{tile.value}</div>
          </div>
        ))}
      </div>
      {(order?.loyaltyPointsAwarded || 0) > 0 && (
        <a href={navigateUrl('/points', pathname)} className="relative mt-3 flex items-center gap-3 rounded-lg backdrop-blur px-3 py-2.5 hover:opacity-90 transition-opacity" style={{ background: overlay }}>
          <div className="w-8 h-8 rounded-full grid place-items-center shrink-0" style={{ background: overlayStrong }}>
            <Coins className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-bold tabular-nums">+{formatNumber(order.loyaltyPointsAwarded)} оноо</span>
            <span style={{ color: 'rgba(255,255,255,0.85)' }}> энэ захиалгаас цуглууллаа</span>
          </div>
          <ArrowRight className="w-4 h-4 shrink-0" />
        </a>
      )}
    </div>
  )
}

function Countdown({ countdown }: { countdown: { h: number, m: number, s: number, expired: boolean } }) {
  const pad = (n: number) => String(n).padStart(2, '0')
  const units = [
    { v: countdown.h, lab: 'цаг' },
    { v: countdown.m, lab: 'мин' },
    { v: countdown.s, lab: 'сек' },
  ]
  return (
    <div className="rounded-xl p-4 bg-[#fffbeb] border border-[#fde68a] flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <Clock className="w-5 h-5 text-[#92400e] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-semibold text-[#92400e]">Төлбөр хүлээгдэж байна</div>
          <div className="text-xs text-[#a16207] mt-0.5">Доорх дансруу шилжүүлэг хийж, захиалгаа баталгаажуулна уу.</div>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:flex sm:items-baseline gap-1 tabular-nums">
        {units.map((u, i) => (
          <div key={i} className="flex flex-col items-center px-2 py-1 rounded-md bg-white border border-[#fde68a]">
            <span className="text-base font-bold text-[#92400e] leading-none">{pad(u.v)}</span>
            <span className="text-[9px] text-[#a16207] mt-0.5">{u.lab}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function BankTransferCard({ order, primary }: { order: any, primary: string }) {
  const bank = order?.vendor?.bank
  const bankName = bank?.bankName || '—'
  const bankIcon = bankList.find(b => b.code == bank?.bankCode)?.icon
  const reference = `#${order?.customId}`

  return (
    <div className="relative">
      <div className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden">
        <div className="px-5 pt-5 pb-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg overflow-hidden border border-border bg-white grid place-items-center shrink-0">
            {bankIcon && <Image src={bankIcon} width={44} height={44} alt={bankName} className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Хүлээн авагч банк</div>
            <div className="text-base font-semibold text-foreground truncate">{bankName}</div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
            <ShieldCheck className="w-4 h-4 text-green-600" />Баталгаажсан
          </div>
        </div>

        <div className="px-5 pb-5 grid gap-3">
          <CopyField label="IBAN · Дансны дугаар" value={bank?.accountIban || ''} mono />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Хүлээн авагч</div>
              <div className="bg-white rounded-lg border border-border px-3 py-2.5 text-sm font-semibold text-foreground">{bank?.accountName || '—'}</div>
            </div>
            <CopyField label="Гүйлгээний утга" value={reference} mono />
          </div>
        </div>

        <div
          className="h-px mx-3"
          style={{
            backgroundImage: 'linear-gradient(to right, var(--border) 50%, transparent 50%)',
            backgroundSize: '10px 1px',
            backgroundRepeat: 'repeat-x',
            backgroundPosition: 'center',
          }}
        />

        <div className="px-5 py-5" style={{ background: primary, color: '#ffffff' }}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.85)' }}>Шилжүүлэх дүн</div>
              <div className="text-3xl font-bold tabular-nums tracking-tight">{formatPrice(getPayableAmount(order))}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.85)' }}>Захиалга</div>
              <div className="text-base font-mono font-semibold">#{order?.customId}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-start gap-2 px-2 text-xs text-muted-foreground">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <p>
          Гүйлгээний утга хэсэгт <b className="font-semibold text-foreground">#{order?.customId}</b> захиалгын дугаарыг заавал бичнэ үү. Төлбөр хүлээн авмагц захиалга баталгаажна.
        </p>
      </div>
    </div>
  )
}

/* ---------- Order items ---------- */

function ProductItem({ product, isFirst, vendor, pathname, order }: { product: any, isFirst: boolean, vendor: any, pathname: string, order: any }) {
  const [downloading, setDownloading] = useState(false)
  const productData = product.product || product
  const productName = productData?.name || product?.name || 'Бүтээгдэхүүн'
  const productCustomId = productData?.customId || product?.customId
  const variant = product.variant
  const isReward = product.isRewardItem
  const discount = !isReward ? product.discount : null
  const hasDiscount = discount && (discount.type === 'productDiscount' || discount.type === 'order')

  const { unitPrice, originalUnitPrice, discountApplied } = getProductPricing(product)
  const dimension = getVariantDimension(variant)
  const visibleCustomFields = (product.customFields || []).filter(isCustomFieldVisible)
  const isFullyRefunded = product?.refunded && product?.quantity === 0
  const isPartiallyRefunded = product?.refunded && product?.quantity > 0
  const showBadgeRow = isReward || (hasDiscount && discountApplied) || productData?.isDigital || isFullyRefunded || isPartiallyRefunded
  const downloadStatus = productData?.isDigital && variant?.id
    ? (order?.digitalDownloadStatus || []).find((s: any) => s?.variantId === variant.id) || null
    : null
  const isExpired = !!downloadStatus?.expired
  const isLimitHit = !!downloadStatus && downloadStatus.downloadsRemaining === 0 && !isExpired
  const downloadAllowed = productData?.isDigital
    && variant?.digitalFile
    && DIGITAL_DOWNLOAD_STATUSES.has(order?.status)
    && !isExpired
    && !isLimitHit
  const downloadUrl = downloadAllowed ? buildDigitalDownloadUrl(order, vendor, variant.id) : null

  return (
    <li className={'flex gap-3 px-4 sm:px-5 py-4 ' + (isFirst ? '' : 'border-t border-border') + (isFullyRefunded ? ' bg-zinc-50/60' : '')}>
      <a href={navigateUrl(`/product/${productCustomId}`, pathname)} className="relative shrink-0">
        <div className="relative w-16 h-16 sm:w-20 sm:h-20">
          <img
            src={formatImageURL(variant?.images?.[0] || 'placeholder.png')}
            alt={productName}
            className={'w-full h-full rounded-lg object-cover bg-zinc-100 border border-border ' + (isFullyRefunded ? 'opacity-50 grayscale' : '')}
          />
          {isReward && (
            <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-orange-600 text-white grid place-items-center">
              <Gift className="w-3 h-3" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </a>
      <div className="flex-1 min-w-0">
        <a href={navigateUrl(`/product/${productCustomId}`, pathname)} className={'block text-sm font-semibold leading-snug line-clamp-2 hover:underline ' + (isFullyRefunded ? 'text-muted-foreground line-through' : 'text-foreground')}>{productName}</a>
        {variant?.name && (
          <div className="text-xs text-muted-foreground mt-0.5">
            {variant.name}{dimension ? ` · ${dimension}` : ''}
          </div>
        )}
        {showBadgeRow && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {isFullyRefunded && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 text-[11px] font-semibold">
                <RotateCcw className="w-3 h-3" strokeWidth={2.5} />
                <span>Буцаагдсан</span>
              </span>
            )}
            {isPartiallyRefunded && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 text-[11px] font-semibold">
                <RotateCcw className="w-3 h-3" strokeWidth={2.5} />
                <span>Хэсэгчлэн буцаасан · {product?.refundedQuantity}ш</span>
              </span>
            )}
            {isReward && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-green-100 text-green-700 text-[11px] font-semibold">
                <Gift className="w-3 h-3" strokeWidth={2.5} />
                <span className="line-clamp-1">{product.discountName || 'Бэлэг'}</span>
              </span>
            )}
            {hasDiscount && discountApplied && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-700 text-[11px] font-semibold">
                <CirclePercent className="w-3 h-3" strokeWidth={2.5} />
                <span className="line-clamp-1">{discount.name}</span>
              </span>
            )}
            {productData?.isDigital && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 text-[11px] font-semibold whitespace-nowrap">Дижитал</span>
            )}
          </div>
        )}
        {visibleCustomFields.length > 0 && (
          <div className="mt-2 space-y-1">
            {visibleCustomFields.map((field: any, i: number) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="text-muted-foreground font-medium">{field.name}:</span>
                <span className="text-foreground">{renderCustomFieldValue(field, formatDateLong)}</span>
              </div>
            ))}
          </div>
        )}
        {productData?.isDigital && variant?.digitalFile && DIGITAL_DOWNLOAD_STATUSES.has(order?.status) && (
          <div className="mt-2 flex flex-col gap-1.5">
            {downloadUrl ? (
              <Button tag="button" isLoading={downloading} loadingMessage="Татаж байна..." onClick={async () => { setDownloading(true); await downloadDigitalFile(downloadUrl); setDownloading(false) }} border={vendor?.border} color={vendor?.color?.primary} className="h-8! px-3! text-xs! w-fit">
                <Download className="w-4 h-4" />
                <span>Файл татах</span>
              </Button>
            ) : (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-muted text-muted-foreground text-xs font-medium w-fit">
                <Download className="w-3.5 h-3.5" />
                <span>{isExpired ? 'Татах хугацаа дууссан байна' : 'Татах хязгаар хэтэрсэн байна'}</span>
              </div>
            )}
            {downloadStatus && !isExpired && (
              <div className="text-[11px] text-muted-foreground tabular-nums">
                Үлдсэн: <span className="font-semibold text-foreground">{downloadStatus.downloadsRemaining}/{downloadStatus.downloadsMax}</span>
                {downloadStatus.expiresAt && (
                  <span> · {formatDateNormal(new Date(downloadStatus.expiresAt))} хүртэл</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <ProductItemPrice product={product} unitPrice={unitPrice} originalUnitPrice={originalUnitPrice} variant={variant} isReward={isReward} />
    </li>
  )
}

function ProductItemPrice({ product, unitPrice, originalUnitPrice, variant, isReward }: { product: any, unitPrice: number, originalUnitPrice: number | null, variant: any, isReward: boolean }) {
  const hasDiscountedPrice = originalUnitPrice !== null && unitPrice < originalUnitPrice
  const isFullyRefunded = product?.refunded && product?.quantity === 0
  return (
    <div className="text-right shrink-0 flex flex-col justify-between min-w-20">
      <div className={isFullyRefunded ? 'opacity-60' : ''}>
        {isReward ? (
          <>
            <div className={'text-sm font-semibold ' + (isFullyRefunded ? 'text-muted-foreground line-through' : 'text-green-600')}>{unitPrice === 0 ? 'Үнэгүй' : formatPrice(unitPrice)}</div>
            <div className="text-xs text-muted-foreground line-through">{formatPrice(originalUnitPrice)}</div>
          </>
        ) : hasDiscountedPrice ? (
          <>
            <div className={'text-sm font-semibold ' + (isFullyRefunded ? 'text-muted-foreground line-through' : (unitPrice === 0 ? 'text-green-600' : 'text-red-500'))}>{unitPrice === 0 ? 'Үнэгүй' : formatPrice(unitPrice)}</div>
            <div className="text-xs text-muted-foreground line-through">{formatPrice(originalUnitPrice)}</div>
          </>
        ) : (
          <div className={'text-sm font-semibold ' + (isFullyRefunded ? 'text-muted-foreground line-through' : 'text-foreground')}>{formatPrice(variant?.price || 0)}</div>
        )}
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        × {(product?.quantity || 0) + (product?.refundedQuantity || 0)} ш.
      </div>
    </div>
  )
}

function OrderItems({ order, vendor, pathname }: { order: any, vendor: any, pathname: string }) {
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="font-semibold text-sm flex items-center gap-2 text-foreground">
          <Package className="w-4 h-4" />Захиалсан бараа
          <span className="text-muted-foreground font-normal">({order?.products?.length || 0})</span>
        </div>
      </div>
      <ul>
        {order?.products?.map((product: any, i: number) => (
          <ProductItem key={i} product={product} isFirst={i === 0} vendor={vendor} pathname={pathname} order={order} />
        ))}
      </ul>
    </div>
  )
}

/* ---------- Order info card ---------- */

function OrderInfoCard({ order }: { order: any }) {
  const isPickup = order?.type === 'pickup'
  const orderProducts = order?.products || []
  const allDigital = orderProducts.length > 0 && orderProducts.every((p: any) => (p.product || p)?.isDigital === true)
  return (
    <details className="rounded-xl border border-border bg-white overflow-hidden group" open>
      <summary className="px-4 sm:px-5 py-3 flex items-center justify-between border-b border-border cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <div className="font-semibold text-sm flex items-center gap-2 text-foreground"><UserIcon className="w-4 h-4" />Захиалгын мэдээлэл</div>
        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-4 sm:px-5 py-1 divide-y divide-border">
        <InfoRow icon={UserIcon} label="Хүлээн авагч">
          {getCustomerName(order) || '-'}
        </InfoRow>
        <InfoRow icon={Phone} label="Утас">
          {order?.phone}
          {order?.additionalPhone ? <span className="text-muted-foreground"> · {order.additionalPhone}</span> : null}
        </InfoRow>
        {order?.email && <InfoRow icon={Mail} label="Имэйл">{order.email}</InfoRow>}
        {!isPickup && !allDigital && (
          <InfoRow icon={MapPin} label="Хүргэх хаяг">
            <div>{formatOrderAddress(order?.address, order?.channel)}</div>
            {order?.address?.type && !FREEFORM_ADDRESS_CHANNELS.includes(order?.channel) && (
              <div className="text-xs text-muted-foreground mt-0.5">{formatAddressType(order.address.type)}</div>
            )}
          </InfoRow>
        )}
        {isPickup && !allDigital && (order?.branchData || order?.branch) && (
          <InfoRow icon={Store} label="Авах салбар">
            {`${order?.branchData?.name || order?.branch?.name}, ${order?.branchData?.address || order?.branch?.address}`}
          </InfoRow>
        )}
        {(order?.orderCustomFields || []).filter(isCustomFieldVisible).map((field: any, i: number) => (
          <InfoRow key={i} icon={Info} label={field.name}>
            {renderCustomFieldValue(field, formatDateTimeLong)}
          </InfoRow>
        ))}
        <InfoRow icon={CreditCard} label="Төлбөрийн хэлбэр">
          {PAYMENT_METHOD_LABELS[order?.paymentMethod] || PAYMENT_METHOD_LABELS.transfer}
        </InfoRow>
        <InfoRow icon={Clock} label="Үүсгэсэн">{formatDateTimeLong(order?.createdAt)}</InfoRow>
        {order?.paidAt && <InfoRow icon={CircleCheck} label="Төлсөн">{formatDateTimeLong(order.paidAt)}</InfoRow>}
        {order?.readyAt && <InfoRow icon={Package} label="Бэлэн болсон">{formatDateTimeLong(order.readyAt)}</InfoRow>}
        {order?.shippedAt && <InfoRow icon={Truck} label="Хүргэлтэнд гарсан">{formatDateTimeLong(order.shippedAt)}</InfoRow>}
        {order?.deliveredAt && <InfoRow icon={Home} label="Хүргэгдсэн">{formatDateTimeLong(order.deliveredAt)}</InfoRow>}
        {order?.canceledAt && <InfoRow icon={XCircle} label="Цуцлагдсан">{formatDateTimeLong(order.canceledAt)}</InfoRow>}
        {order?.refundedAt && <InfoRow icon={RotateCcw} label="Буцаагдсан">{formatDateTimeLong(order.refundedAt)}</InfoRow>}
        {order?.deliveryFailedAt && <InfoRow icon={XCircle} label="Хүргэлт амжилтгүй">{formatDateTimeLong(order.deliveryFailedAt)}</InfoRow>}
        {order?.deliveryFailedReason && <InfoRow icon={Info} label="Амжилтгүй болсон шалтгаан">{order.deliveryFailedReason}</InfoRow>}
      </div>
    </details>
  )
}

/* ---------- Price breakdown ---------- */

function PriceBreakdown({ order, vendor }: { order: any, vendor: any }) {
  const { salePriceSavings, discountBreakdown } = calcPriceBreakdown(order)
  const totalDue = (order?.amount || 0) - (order?.totalRefunded || 0)
  const isDelivery = order?.type === 'delivery'
  const deposit = calcDepositBreakdown(order)

  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-border font-semibold text-sm flex items-center gap-2 text-foreground">
        <Receipt className="w-4 h-4" />Төлбөрийн задаргаа
      </div>
      <dl className="px-4 sm:px-5 py-3 text-sm divide-y divide-border">
        <PriceRow label="Барааны дүн" value={formatPrice(order?.subAmount)} />
        {salePriceSavings > 0 && <PriceRow label="Хямдралын дүн" value={`-${formatPrice(salePriceSavings)}`} tone="green" />}
        {discountBreakdown.map(d => (
          <PriceRow key={d.id} label={`Урамшуулал (${d.name})`} value={`-${formatPrice(d.amount)}`} tone="green" />
        ))}
        {(order?.couponAmount || 0) > 0 && (
          <PriceRow label={`Купон${order?.couponData?.name ? ` (${order.couponData.name})` : ''}`} value={`-${formatPrice(order?.couponAmount)}`} tone="blue" />
        )}
        {(order?.giftCardAmount || 0) > 0 && (
          <PriceRow label="Бэлгийн карт" value={`-${formatPrice(order?.giftCardAmount)}`} tone="purple" />
        )}
        {(order?.loyaltyDiscountAmount || 0) > 0 && (
          <PriceRow label="Лоялти хөнгөлөлт" value={`-${formatPrice(order?.loyaltyDiscountAmount)}`} tone="green" />
        )}
        {(order?.loyaltyVoucherAmount || 0) > 0 && (
          <PriceRow label="Лоялти ваучер" value={`-${formatPrice(order?.loyaltyVoucherAmount)}`} tone="purple" />
        )}
        {isDelivery && (
          <PriceRow
            label="Хүргэлтийн үнэ"
            value={vendor?.deliveryType === 'laterDelivery' && order?.deliveryPrice === 0 ? <span className="text-amber-600">Нэмэлтээр тооцно</span> : order?.deliveryPrice === 0 ? <span className="text-green-700">Үнэгүй</span> : formatPrice(order?.deliveryPrice)}
          />
        )}
        {(order?.totalRefunded || 0) > 0 && (
          <PriceRow label="Буцаалт" value={`-${formatPrice(order?.totalRefunded)}`} tone="red" />
        )}
      </dl>
      <div className="px-4 sm:px-5 py-3 border-t border-border bg-zinc-50 flex items-baseline justify-between">
        <div className="text-sm font-semibold text-foreground">Нийт төлөх</div>
        <div className="text-2xl font-bold tabular-nums text-foreground">{formatPrice(totalDue)}</div>
      </div>
      {/* Split payment: how that total divides. Below the grand total, never in place of
          it — the deposit and balance are a split of what is owed, not a change to it. */}
      {deposit.isDeposit && (
        <div className="px-4 sm:px-5 py-3 border-t border-border flex flex-col gap-2 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="text-muted-foreground flex items-center gap-2">
              {DEPOSIT_LABELS.deposit}
              {deposit.isDepositPaid
                ? <span className="text-[11px] text-green-700 bg-green-50 rounded px-1.5 py-0.5">Төлсөн</span>
                : <span className="text-[11px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">Хүлээгдэж буй</span>}
            </span>
            <span className={'tabular-nums font-medium ' + (deposit.isDepositPaid ? 'text-green-700' : '')}>{formatPrice(deposit.depositAmount)}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className={'flex items-center gap-2 ' + (deposit.outstanding > 0 ? 'font-semibold text-amber-700' : 'text-muted-foreground')}>
              {deposit.outstanding > 0 ? DEPOSIT_LABELS.balanceDue : DEPOSIT_LABELS.balance}
              {deposit.isFullyPaid && <span className="text-[11px] text-green-700 bg-green-50 rounded px-1.5 py-0.5">Төлсөн</span>}
            </span>
            <span className={'tabular-nums font-semibold ' + (deposit.isFullyPaid ? 'text-green-700' : deposit.outstanding > 0 ? 'text-amber-700' : 'text-muted-foreground')}>
              {formatPrice(deposit.balanceAmount) || '0₮'}
            </span>
          </div>
          {order?.depositTerms && (
            /* Vendor-authored plain text — rendered as text, never as HTML. */
            <p className="text-xs text-muted-foreground whitespace-pre-line mt-1 pt-2 border-t border-border">{order.depositTerms}</p>
          )}
        </div>
      )}
    </div>
  )
}

const PRICE_ROW_TONES = {
  default: { label: 'text-muted-foreground', value: 'text-foreground' },
  green: { label: 'text-green-700', value: 'text-green-700' },
  blue: { label: 'text-blue-600', value: 'text-blue-600' },
  purple: { label: 'text-purple-700', value: 'text-purple-700' },
  red: { label: 'text-red-600', value: 'text-red-600' },
} as const

function PriceRow({ label, value, tone = 'default' }: { label: string, value: any, tone?: keyof typeof PRICE_ROW_TONES }) {
  const t = PRICE_ROW_TONES[tone]
  return (
    <div className="flex justify-between py-1.5">
      <dt className={t.label}>{label}</dt>
      <dd className={'tabular-nums ' + t.value}>{value}</dd>
    </div>
  )
}

/* ---------- Pending payment methods (qpay/storepay/pocket/lend/omniway) ---------- */

function OnDeliveryCard({ order }: { order: any }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full bg-amber-50 text-amber-700 grid place-items-center shrink-0">
        <HandCoins className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-foreground">Барааг авахдаа төлөх</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Захиалгын дүн <strong>{formatPrice(order?.amount)}</strong>-г барааг хүлээн авахдаа төлнө үү. Урьдчилж төлөх шаардлагагүй.
        </div>
      </div>
    </div>
  )
}

function PendingPaymentBlock({ order, vendor, onRefresh, primary }: { order: any, vendor: any, onRefresh: () => void, primary: string }) {
  /*
    Split payment is checked before payAfterDelivery, and the order matters.

    A deposit order flips payAfterDelivery on the moment the deposit lands, so once the
    balance is outstanding the onDelivery branch below would swallow it and tell a
    customer who owes money to "pay at the door" — with no way to pay online at all.

    While the deposit itself is still due the order has payAfterDelivery = false, so it
    falls through to the ordinary rail blocks; they render getPayableAmount, which is the
    deposit at that point. Nothing special needed for that state.
  */
  const deposit = calcDepositBreakdown(order)
  if (deposit.isAwaitingBalance && !hasLiveInvoice(order)) {
    return <DepositBalanceCard order={order} vendor={vendor} onRefresh={onRefresh} />
  }
  if (order?.payAfterDelivery && !deposit.isDeposit) return <OnDeliveryCard order={order} />
  if (order?.paymentMethod === 'qpay') return <QpayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'storepay') return <StorepayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'pocket') return <PocketBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.paymentMethod === 'lend') return <LendBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  // A cancelled OmniWay invoice is not payable — fall through to the fallback cards below.
  if (order?.paymentMethod === 'omniway' && !order?.omniway?.canceledAt) return <OmniwayBlock order={order} vendor={vendor} onRefresh={onRefresh} />
  if (order?.payAfterDelivery) return <OnDeliveryCard order={order} />
  return <BankTransferCard order={order} primary={primary} />
}

function CheckPaymentButton({ loading, success, onClick, vendor }: { loading: boolean, success: boolean, onClick: () => void, vendor: any }) {
  return (
    <Button onClick={onClick} disabled={loading} border={vendor?.border} color={vendor?.color?.primary} className="w-full h-12 sm:h-10">
      {loading ? 'Шалгаж байна...' : success ? 'Төлбөр төлөгдсөн байна' : 'Төлбөр шалгах'}
    </Button>
  )
}

function PaymentSuccessCard() {
  return (
    <div className="rounded-2xl bg-white border border-green-200 p-5 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full bg-green-50 text-green-700 grid place-items-center shrink-0">
        <CircleCheck className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-foreground">Төлбөр амжилттай төлөгдлөө</div>
        <div className="text-xs text-muted-foreground mt-0.5">Захиалга баталгаажиж, бид удахгүй боловсруулж эхэлнэ.</div>
      </div>
    </div>
  )
}

function MethodCardHeader({ logo, title, subtitle, badge }: { logo: any, title: string, subtitle?: string, badge?: any }) {
  return (
    <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-border">
      <div className="w-11 h-11 rounded-lg bg-zinc-50 border border-border grid place-items-center shrink-0 p-2 overflow-hidden">{logo}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Төлбөрийн арга</div>
        <div className="text-base font-semibold text-foreground truncate">{title}{subtitle ? <span className="text-muted-foreground font-normal"> · {subtitle}</span> : null}</div>
      </div>
      {badge}
    </div>
  )
}

// `image`: value is a ready-made QR image (data URI) from the provider, not a string to encode.
function QRFrame({ value, accent, logoSrc, size = 200, image }: { value: string, accent: string, logoSrc?: any, size?: number, image?: boolean }) {
  if (!value) {
    return (
      <div className="rounded-lg bg-zinc-100 animate-pulse" style={{ width: size + 32, height: size + 32 }} />
    )
  }
  const corners = [
    { pos: { top: 0, left: 0 }, border: 'border-t-2 border-l-2 rounded-tl-xl' },
    { pos: { top: 0, right: 0 }, border: 'border-t-2 border-r-2 rounded-tr-xl' },
    { pos: { bottom: 0, left: 0 }, border: 'border-b-2 border-l-2 rounded-bl-xl' },
    { pos: { bottom: 0, right: 0 }, border: 'border-b-2 border-r-2 rounded-br-xl' },
  ]
  return (
    <div className="relative inline-block" style={{ width: size + 32, height: size + 32 }}>
      <div className="absolute inset-0 pointer-events-none">
        {corners.map((c, i) => (
          <div key={i} className={'absolute w-5 h-5 ' + c.border} style={{ ...c.pos, borderColor: accent }} />
        ))}
      </div>
      <div className="absolute inset-2 rounded-lg bg-white p-3 shadow-sm">
        {image
          ? <img src={value} alt="QR" className="w-full h-full object-contain" />
          : <QRCode value={value} size={size - 8} fgColor={accent} bgColor="#ffffff" style={{ width: '100%', height: '100%' }} />}
        {logoSrc && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-md bg-white border border-border shadow grid place-items-center p-1">
            <Image src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        )}
      </div>
    </div>
  )
}

function NumberedSteps({ steps, accent, mutedNumbers }: { steps: string[], accent: string, mutedNumbers?: boolean }) {
  return (
    <ol className="space-y-2 text-sm text-foreground">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5">
          <span
            className={'shrink-0 w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold mt-0.5 ' + (mutedNumbers ? 'bg-zinc-100 text-muted-foreground' : 'text-white')}
            style={mutedNumbers ? undefined : { background: accent }}
          >
            {i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  )
}

function BankChips({ banks }: { banks: any[] }) {
  const visible = banks.slice(0, 8)
  const hidden = banks.slice(8)
  return (
    <div className="group/chips flex items-center gap-1.5 min-w-0">
      <div className="flex items-center -space-x-2">
        {visible.map((b: any, i: number) => (
          <div
            key={i}
            className="w-7 h-7 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm shrink-0 transition-transform duration-300 ease-out hover:scale-110 hover:z-10"
            title={b.name}
          >
            <img src={b.logo} alt={b.name || ''} className="w-full h-full object-cover" />
          </div>
        ))}
        <div className="grid grid-cols-[0fr] group-hover/chips:grid-cols-[1fr] transition-[grid-template-columns] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] justify-end">
          <div className="overflow-hidden flex items-center -space-x-2">
            {hidden.map((b: any, i: number) => (
              <div
                key={i}
                className="w-7 h-7 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm shrink-0 opacity-0 translate-x-2 group-hover/chips:opacity-100 group-hover/chips:translate-x-0 transition-[opacity,transform] duration-300 ease-out"
                style={{ transitionDelay: `${(hidden.length - 1 - i) * 25}ms` }}
                title={b.name}
              >
                <img src={b.logo} alt={b.name || ''} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </div>
      {hidden.length > 0 && (
        <span className="inline-flex items-center justify-center min-w-7 h-7 px-1.5 rounded-full border-2 border-white bg-zinc-100 text-[10px] font-semibold text-muted-foreground shadow-sm shrink-0 -ml-1.5 group-hover/chips:opacity-0 group-hover/chips:scale-90 group-hover/chips:-mr-9 transition-[opacity,transform,margin] duration-300 ease-out">
          +{hidden.length}
        </span>
      )}
    </div>
  )
}

function QpayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'qpay', onRefresh })
  const { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser } = useInAppBrowser({ order })
  if (success) return <PaymentSuccessCard />
  if (!order) return null
  const banks = order?.qpay?.urls || []
  return (
    <div className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden">
      <MethodCardHeader
        logo={<Image src={qpayLogo} alt="QPay" className="w-full h-full object-contain" />}
        title="QPay"
        subtitle="QR төлбөр"
        badge={<div className="hidden sm:flex items-center gap-1.5 text-xs text-green-600 font-medium"><Zap className="w-4 h-4" />Шуурхай</div>}
      />

      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={order?.qpay?.QRCode} accent="#0f172a" logoSrc={qpayLogo} size={200} />
          <div className="mt-2 text-[11px] text-muted-foreground font-semibold text-center">
            Захиалга <span className="font-mono text-foreground">#{order.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Төлөх дүн</div>
          <div className="text-3xl font-bold tabular-nums tracking-tight text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-3">
            <NumberedSteps
              accent="#0f172a"
              mutedNumbers
              steps={[
                'Банкны аппаа нээгээд QR уншуулах цэс рүү орно уу',
                'Дээрх QR кодыг уншуулан төлбөрөө баталгаажуулна уу',
              ]}
            />
          </div>
          <div className="mt-4"><CheckPaymentButton loading={loading} success={success} onClick={check} vendor={vendor} /></div>
        </div>
      </div>

      {banks.length > 0 && (
        <>
          <div className="px-5 pb-5 sm:hidden">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Банкны апп шууд нээх</div>
            {isInAppBrowser && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-3">
                <p className="text-xs text-amber-900">{IN_APP_BROWSER_LABELS.message}</p>
                <a href={browserUrl} onClick={openInBrowser} className="mt-2.5 inline-flex items-center gap-2 justify-center text-sm font-semibold text-white bg-slate-900 rounded-md py-2.5 px-4 hover:opacity-95">
                  {IN_APP_BROWSER_LABELS.action}<ExternalLink className="w-3.5 h-3.5" />
                </a>
                <p className="text-[11px] text-amber-800 mt-2.5">{IN_APP_BROWSER_LABELS.hint}</p>
              </div>
            )}
            {!appLinksBlocked && (
              <div className="grid grid-cols-6 gap-2">
                {banks.map((b: any, i: number) => (
                  <a key={i} href={b.link} className="aspect-square rounded-lg bg-white border border-border overflow-hidden hover:border-blue-300 transition-colors">
                    <img src={b.logo} alt={b.name || `Bank ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="hidden sm:flex px-5 pb-5 items-center justify-between border-t border-dashed border-border pt-4 gap-3">
            <div className="text-xs text-muted-foreground shrink-0">Дэмжигдсэн банкууд</div>
            <BankChips banks={banks} />
          </div>
        </>
      )}
    </div>
  )
}

function PocketBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'pocket', onRefresh })
  const accent = '#e42f56'
  if (success) return <PaymentSuccessCard />
  return (
    <div className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden">
      <MethodCardHeader
        logo={<Image src={pocketzeroLogo} alt="PocketZero" className="w-full h-full object-contain" />}
        title="PocketZero"
        subtitle="Хүүгүй зээл"
        badge={<span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#fef2f2', color: accent }}>0% ХҮҮТЭЙ</span>}
      />

      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={order?.pocket?.qr} accent={accent} logoSrc={pocketzeroLogo} size={200} />
          <div className="mt-2 text-[11px] text-muted-foreground font-semibold text-center">
            Захиалга <span className="font-mono text-foreground">#{order?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Зээлээр төлөх</div>
          <div className="text-3xl font-bold tabular-nums tracking-tight text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-2 text-xs text-muted-foreground">Сард тэнцүү хуваан төлөх боломжтой. Нэмэлт хүү, шимтгэлгүй.</div>
          <div className="mt-3">
            <NumberedSteps
              accent={accent}
              steps={[
                'Pocket аппыг нээж PocketZero цэс рүү орно уу',
                'Дээрх QR кодыг уншуулж нөхцөлөө баталгаажуулна уу',
              ]}
            />
          </div>
          {order?.pocket?.deeplink && (
            <a
              href={order.pocket.deeplink}
              className="mt-4 lg:hidden inline-flex items-center gap-2 justify-center text-sm font-semibold text-white rounded-md py-2.5 px-4 hover:opacity-95 self-start"
              style={{ background: accent }}
            >
              <Image src={pocketzeroLogo} alt="" className="w-4 h-4 object-contain" />Pocket App нээх<ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <div className="mt-4"><CheckPaymentButton loading={loading} success={success} onClick={check} vendor={vendor} /></div>
        </div>
      </div>
    </div>
  )
}

function OmniwayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method: 'omniway', onRefresh })
  const accent = '#ffc800'
  if (success) return <PaymentSuccessCard />
  // A cancelled invoice can no longer be paid — never show its stale QR.
  if (order?.omniway?.canceledAt) return null
  return (
    <div className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden">
      <MethodCardHeader
        logo={<Image src={omniwayLogo} alt="OmniWay" className="w-full h-full object-contain" />}
        title="OmniWay"
        subtitle="Хуваан төлөх"
      />

      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={getOmniwayQrImage(order)} accent={accent} size={200} image />
          <div className="mt-2 text-[11px] text-muted-foreground font-semibold text-center">
            Захиалга <span className="font-mono text-foreground">#{order?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Зээлээр төлөх</div>
          <div className="text-3xl font-bold tabular-nums tracking-tight text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-3 text-sm text-foreground">OmniWay аппликэйшнээр QR кодыг уншуулж төлнө үү</div>
          {order?.omniway?.qrContent && (
            <a
              href={order.omniway.qrContent}
              className="mt-4 lg:hidden inline-flex items-center gap-2 justify-center text-sm font-semibold text-zinc-900 rounded-md py-2.5 px-4 hover:opacity-95 self-start"
              style={{ background: accent }}
            >
              <Image src={omniwayLogo} alt="" className="w-4 h-4 object-contain rounded-sm" />OmniWay-ээр төлөх<ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <div className="mt-4"><CheckPaymentButton loading={loading} success={success} onClick={check} vendor={vendor} /></div>
        </div>
      </div>
    </div>
  )
}

type PhoneMethodConfig = {
  brand: string
  tagline: string
  appName: string
  accent: string
  logo: any
  phone: string
  steps: string[]
}

function PhoneMethodCard({ order, vendor, method, config, onRefresh }: { order: any, vendor: any, method: PaymentEndpoint, config: PhoneMethodConfig, onRefresh: () => void }) {
  const { loading, success, check } = usePaymentChecker({ order, vendor, method, onRefresh })
  if (success) return <PaymentSuccessCard />
  return (
    <div className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden">
      <MethodCardHeader logo={config.logo} title={config.brand} subtitle={config.tagline} />

      <div className="p-5 grid sm:grid-cols-2 gap-5">
        <div className="flex flex-col">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Зээлээр төлөх</div>
          <div className="text-3xl font-bold tabular-nums tracking-tight text-foreground">{formatPrice(getPayableAmount(order))}</div>
          <div className="mt-5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Бүртгэлтэй утасны дугаар</div>
          <div className="relative">
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <div className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-border text-sm font-mono tabular-nums font-semibold bg-zinc-50 text-foreground">
              {config.phone || '—'}
            </div>
          </div>
          <div className="mt-1.5 text-[11px] text-muted-foreground">{config.appName} апп дээр бүртгэлтэй утасны дугаар.</div>
          <div className="mt-4"><CheckPaymentButton loading={loading} success={success} onClick={check} vendor={vendor} /></div>
        </div>

        <div className="relative rounded-xl p-5" style={{ background: `linear-gradient(145deg, ${config.accent}1f, ${config.accent}08)` }}>
          <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: config.accent }}>Хэрхэн төлөх</div>
          <NumberedSteps accent={config.accent} steps={config.steps} />
          <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
            <Smartphone className="w-3.5 h-3.5" style={{ color: config.accent }} />
            Мэдэгдэл таны утсанд хэдхэн секундийн дотор очно.
          </div>
        </div>
      </div>
    </div>
  )
}

function StorepayBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  return (
    <PhoneMethodCard
      order={order}
      vendor={vendor}
      method="storepay"
      onRefresh={onRefresh}
      config={{
        brand: 'StorePay',
        tagline: 'Хуваан төлөх үйлчилгээ',
        appName: 'StorePay',
        accent: '#0ea5e9',
        phone: order?.storepay?.phone,
        logo: <Image src={storepayLogo} alt="StorePay" className="w-full h-full object-contain" />,
        steps: [
          'Бүртгэлтэй утасны дугаар нь мөн эсэхийг шалгах',
          'StorePay апп дээрх ирсэн нэхэмжлэлийн Төлөх товчлуурыг дарна',
          'Хугацаагаа сонгож, зээлээ баталгаажуулна',
        ],
      }}
    />
  )
}

function LendBlock({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh: () => void }) {
  return (
    <PhoneMethodCard
      order={order}
      vendor={vendor}
      method="lend"
      onRefresh={onRefresh}
      config={{
        brand: 'LendMN',
        tagline: 'Бэлэн мөнгөн зээл',
        appName: 'LendMN',
        accent: '#f97316',
        phone: order?.lend?.phoneNumber,
        logo: <Image src={lendmnLogo} alt="LendMN" className="w-full h-full object-contain" />,
        steps: [
          'Бүртгэлтэй утасны дугаар нь мөн эсэхийг шалгах',
          'LendMN апп дээрх ирсэн нэхэмжлэлээ шалгана',
          'Хугацаагаа сонгож, зээлээ баталгаажуулна',
        ],
      }}
    />
  )
}

/* ---------- Page ---------- */

export default function OrderDetail({ order, vendor }: { order: any, vendor: any }) {
  const router = useRouter()
  const pathname = usePathname()
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0, expired: false })

  useEffect(() => {
    /*
      Split payment: a deposit order stays 'pending' for the whole 7-10 day lead time
      after its deposit lands. settleDeposit nulls expireAt, so the first clause already
      covers it — depositPaidAt is belt-and-braces against a stale cached order, because
      the failure mode is showing a live, paid order as cancelled.
    */
    if (!order?.expireAt || order?.status !== 'pending' || order?.depositPaidAt) return
    const target = new Date(order.expireAt).getTime()
    const tick = () => {
      const diff = Math.max(0, target - Date.now())
      setCountdown({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        expired: diff === 0,
      })
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [order?.expireAt, order?.status, order?.depositPaidAt])

  const isExpired = countdown.expired
  // A split-payment order whose deposit has landed sits in 'pending' for days by design;
  // it must never be rendered as cancelled by an expiry clock it is no longer subject to.
  const baseStatus = isExpired && order?.status === 'pending' && !order?.depositPaidAt ? 'canceled' : order?.status
  // Booklo-aware: advance to the Booklo delivery status when further along than order.status.
  const displayStatus = bookloDisplayStatus(order, baseStatus)
  const isRefundedStatus = order?.status === 'refunded' || order?.status === 'partiallyRefunded'
  // A pay-after-delivery order stays unpaid all the way through fulfilment, so the
  // "pay on handover" reminder has to survive past 'pending'.
  const isUnsettledOnDelivery = order?.payAfterDelivery && !order?.paidAt && order?.status !== 'canceled'
  // isExpired is not a reason to hide the block on a deposit-paid order — that clock
  // stopped applying the moment the deposit landed (see baseStatus above).
  const showPaymentBlock = (!isExpired || !!order?.depositPaidAt) && !isRefundedStatus && (order?.status === 'pending' || isUnsettledOnDelivery)
  const showPaidConfirmation = !isRefundedStatus && ['paid', 'ready', 'shipped', 'delivered'].includes(displayStatus)
  const primary = resolveColorHex(vendor?.color?.primary)

  return (
    <>
      <div className="cont py-8 sm:py-8 max-w-4xl print:hidden">
        <OrderTitle order={order} status={displayStatus} />
        <div className="mt-6"><OrderStepper order={order} vendor={vendor} status={displayStatus} primary={primary} /></div>

        {order?.expireAt && order?.status === 'pending' && !isExpired && !order?.depositPaidAt && (
          <div className="mt-6"><Countdown countdown={countdown} /></div>
        )}

        {showPaymentBlock && (
          <div className="mt-6">
            <PendingPaymentBlock order={order} vendor={vendor} onRefresh={() => router.refresh()} primary={primary} />
          </div>
        )}

        {showPaidConfirmation && (
          <div className="mt-6"><PaidConfirmation order={order} status={displayStatus} primary={primary} /></div>
        )}

        <div className="mt-6"><OrderItems order={order} vendor={vendor} pathname={pathname} /></div>
        <div className="mt-6"><OrderInfoCard order={order} /></div>

        {showPaidConfirmation && (
          <div className="mt-6"><DigitalDownloadRow order={order} vendor={vendor} primary={primary} /></div>
        )}

        {(order?.status === 'refunded' || order?.status === 'partiallyRefunded') && (
          <div className="mt-6"><RefundBanner order={order} /></div>
        )}

        <div className="mt-6"><PriceBreakdown order={order} vendor={vendor} /></div>
        <div className="mt-8"><FooterActions vendor={vendor} pathname={pathname} /></div>
      </div>
      <PrintableReceipt order={order} vendor={vendor} />
    </>
  )
}
